<template>
  <b-form @submit.prevent="onSubmit" class="overflow">
    <ValidationObserver
      #default="{invalid}"
    >
      <div class="p-1">

        <!-- Uploader / Preview -->
        <div class="rounded-4 p-1 position-relative background-file mb-1">
          <div
            class="container-image mx-auto rounded-circle d-flex flex-column align-items-center justify-content-center border border-3 bg-white"
            :class="{ 'opacity-75': dragging }"
            style=""
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
            @click="openFileDialog"
          >
            <template v-if="!image_preview">
              <small class="text-muted">{{ $t('ia.assistants.dropOrClick') }}</small>
              <i class="bi bi-upload mt-1 fs-4 text-secondary"></i>
            </template>
            <img
              v-else
              :src="image_preview"
              alt="preview"
              class="rounded-circle object-fit-cover h-100 w-100"
            />
          </div>
        </div>
    
        <validation-provider name="image" vid="image" v-slot="{ errors: eImg }">
          <b-form-file
            ref="uploader"
            class="d-none"
            accept="image/png,image/jpeg,image/webp"
            @change="onFileChange"
          />
          <div class="invalid-feedback d-block" v-if="eImg[0]">{{ eImg[0] }}</div>
        </validation-provider>
    
        <!-- name -->
  
        <b-form-group :label="$t('name')" label-for="name" class="mb-1">
          <validation-provider :name="$t('name')"
          :vid="$t('name')" #default="{ errors }" rules="required">
            <b-form-input
              id="name"
              name="Nombre"
              v-model.trim="form.name"
              :state="errors.length > 0 ? false : null"
              :class="{ 'is-invalid': errors[0] }"
            ></b-form-input>
            <div class="invalid-feedback d-block">{{ errors[0] }}</div>
          </validation-provider>
        </b-form-group>
    
        <!-- assistant type -->
        <b-form-group :label="$t('ia.assistants.typeAssistant')" class="mb-2">
          <component
            :is="vue_select" 
            class=""
            :options="$t('ia.type_assistant')"
            label="text"
            v-model="form.type_assistant"
            :clearable="false"
          >
          </component>
        </b-form-group>
    
        <!-- Layout dinamic -->
        <div v-for="lay in filteredLayout" :key="lay.key" class="mb-1">
          <h4 class="">{{ lay.title }}</h4>
    
          <div class="row">
            <template>
              <div v-for="formEl in lay.forms" :key="formEl.name" :class="`${formEl.class}`">
                <template v-if="formEl.type === 'select'">
                  <b-form-group :label="formEl.label" :label-for="formEl.name">
                    <ValidationProvider :name="formEl.name" rules="required" :vid="formEl.name" v-slot="{ errors: e }">
                      <component
                        :is="vue_select"
                        :id="formEl.name"
                        v-model="form[formEl.name]"
                        :options="formEl.options"
                        label="text"
                        :state="!e[0]"
                        :class="{ 'is-invalid': e[0] }"
                      ></component>
                      <div class="invalid-feedback d-block">{{ e[0] }}</div>
                    </ValidationProvider>
                  </b-form-group>
                </template>
    
                <template v-else>
                  <b-form-group :label="formEl.label" :label-for="formEl.name">
                    <ValidationProvider :name="formEl.name" rules="required" :vid="formEl.name" v-slot="{ errors: e }">
                      <b-form-textarea
                        :id="formEl.name"
                        v-model.trim="form[formEl.name]"
                        :state="e.length > 0 ? false : null"
                        :class="{ 'is-invalid': e[0] }"
                        rows="4"
                      />
                      <div class="invalid-feedback d-block">{{ e[0] }}</div>
                    </ValidationProvider>
                  </b-form-group>
                </template>
              </div>
            </template>
          </div>
        </div>
    
        <!-- IA Responses -->
        <div class="mb-4">
          <h4 class="mb-1">{{ $t('ia.assistants.responsesAI') }}</h4>
    
          <div class="row">
            <div class="col-4" v-for="grp in responses_ai" :key="grp.name">
              <b-form-group :label="grp.label">
                <ValidationProvider :name="grp.name" rules="required" :vid="grp.name" v-slot="{ errors: e }">
                  <b-form-radio-group
                    :options="grp.options"
                    v-model="form[grp.name]"
                    :name="grp.name"
                    :state="!e[0]"
                    :class="{ 'is-invalid': e[0] }"
                    stacked
                  >
                  </b-form-radio-group>
                  <div class="invalid-feedback d-block">{{ e[0] }}</div>
                </ValidationProvider>
              </b-form-group>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Footer actions -->
      <div class="d-flex justify-content-end container-fixed p-1">
        <b-button type="submit" variant="primary" :disabled="invalid || (!form.image && !isEditMode)">
          <feather-icon :icon="isEditMode ? 'EditIcon' : 'SaveIcon'"></feather-icon> {{ isEditMode ? $t('update') : $t('save') }}
        </b-button>
      </div>
    </ValidationObserver>
  </b-form>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import {
  BFormInput,
  BFormSelect,
  BFormGroup,
  BFormRadioGroup,
  BButton,
  BFormTextarea,
  BFormFile,
  BForm,
} from 'bootstrap-vue';
import { showToast } from '@/libs/utils/others';
import { addAssistant, updateAssistant } from  '@/services/ia';

export default {
  name: 'AddAssistant',
  components: {
    ValidationObserver,
    ValidationProvider,
    BFormInput,
    BFormSelect,
    BFormGroup,
    BFormRadioGroup,
    BButton,
    BFormTextarea,
    BFormFile,
    BForm,
  },
  props: {
    assistant: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      // UI
      dragging: false,
      image_preview: null,
      submitting: false,

      // Form
      vue_select: null,
      form: {
        image: null,
        name: '',
        type_assistant: this.$t('ia.type_assistant')[0],

        // simplified
        age: '',
        gender: '',
        intellect: '',
        mindset: '',
        occupation: '',
        primary_goal: '',
        secondary_goal: '',

        // advanced
        description: '',

        // responses_ai
        formality: '',
        bias: '',
        response_length: '',
      },

      // layout
      layout: [
        {
          title: this.$t('ia.assistants.details'),
          key: 'details',
          forms: [
            { name: 'age', label: this.$t('dashboard.age'), options: this.$t('ia.selects_text.writer_persona.children_selects.persona_age'), type: 'select', class: 'col-6' },
            { name: 'gender', label: this.$t('dashboard.gender'), options: this.$t('ia.selects_text.writer_persona.children_selects.persona_gender'), type: 'select', class: 'col-6' },
            { name: 'intellect', label: this.$t('ia.text.persona_intellect'), options: this.$t('ia.selects_text.writer_persona.children_selects.persona_intellect'), type: 'select', class: 'col-6' },
            { name: 'mindset', label: this.$t('ia.text.persona_mindset'), options: this.$t('ia.selects_text.writer_persona.children_selects.persona_mindset'), type: 'select', class: 'col-6' },
            { name: 'occupation', label: this.$t('ia.text.persona_occupattion'), options: this.$t('ia.selects_text.writer_persona.children_selects.persona_occupattion'), type: 'select', class: 'col-6' },
          ],
        },
        {
          title: this.$t('ia.assistants.goals'),
          key: 'goals',
          forms: [
            { name: 'primary_goal', label: this.$t('ia.text.first_goal'), options: this.$t('ia.selects_text.goals.children_selects.first_goal'), type: 'select', class: 'col-6' },
            { name: 'secondary_goal', label: this.$t('ia.text.second_goal'), options: this.$t('ia.selects_text.goals.children_selects.second_goal'), type: 'select', class: 'col-6' },
          ],
        },
        {
          title: this.$t('campaigns.Description'),
          key: 'description',
          forms: [{ name: 'description', label: this.$t('campaigns.Description'), type: 'textarea', class: 'col-12' }],
        },
      ],

      responses_ai: [
        { name: 'formality', label: this.$t('ia.assistants.formality'), options: this.$t('ia.formality') },
        { name: 'bias', label: this.$t('ia.assistants.bias'), options: this.$t('ia.bias') },
        { name: 'response_length', label: this.$t('ia.assistants.response_length'), options: this.$t('ia.response_length') },
      ],
    };
  },
  computed: {
    isEditMode() {
      return this.assistant !== null;
    },
    
    filteredLayout() {
      if (this.form.type_assistant.value === 'simplified') {
        return this.layout.slice(0, 2);
      }
      if (this.form.type_assistant.value === 'advanced') {
        return [this.layout[2]];
      }
      return this.layout;
    },
  },
  async mounted() {
    this.vue_select = (await import('vue-select')).default;
    
    if (this.assistant) {
      this.initializeFormForEdit();
    }
  },
  watch: {
    assistant: {
      handler(newVal) {
        if (newVal) {
          this.initializeFormForEdit();
        } else {
          this.resetForm();
        }
      },
      immediate: true
    }
  },
  methods: {
    computeSelectOptions(arr) {
      return (arr || []).map((i) => ({ value: i.text, text: i.text }));
    },

    initializeFormForEdit() {
      if (!this.assistant) return;
      
      this.form.name = this.assistant.name || '';
      
      const typeAssistant = this.$t('ia.type_assistant').find(t => t.value === this.assistant.type_assistant);
      if (typeAssistant) {
        this.form.type_assistant = typeAssistant;
      }
      
      if (this.assistant.image_url || this.assistant.image) {
        this.image_preview = this.assistant.image_url;
        this.form.image = null;
      }
      
      if (this.assistant.responses_ai) {
        this.form.formality = this.assistant.responses_ai.formality || '';
        this.form.bias = this.assistant.responses_ai.bias || '';
        this.form.response_length = this.assistant.responses_ai.response_length || '';
      }
      
      if (this.assistant.type_assistant === 'simplified') {
        if (this.assistant.details) {
          this.form.age = this.findSelectOption('ia.selects_text.writer_persona.children_selects.persona_age', this.assistant.details.age);
          this.form.gender = this.findSelectOption('ia.selects_text.writer_persona.children_selects.persona_gender', this.assistant.details.gender);
          this.form.intellect = this.findSelectOption('ia.selects_text.writer_persona.children_selects.persona_intellect', this.assistant.details.intellect);
          this.form.mindset = this.findSelectOption('ia.selects_text.writer_persona.children_selects.persona_mindset', this.assistant.details.mindset);
          this.form.occupation = this.findSelectOption('ia.selects_text.writer_persona.children_selects.persona_occupattion', this.assistant.details.occupation);
        }
        
        if (this.assistant.goals) {
          this.form.primary_goal = this.findSelectOption('ia.selects_text.goals.children_selects.first_goal', this.assistant.goals.primary_goal);
          this.form.secondary_goal = this.findSelectOption('ia.selects_text.goals.children_selects.second_goal', this.assistant.goals.secondary_goal);
        }
      } else if (this.assistant.type_assistant === 'advanced') {
        this.form.description = this.assistant.description || '';
      }
    },

    findSelectOption(translationKey, value) {
      const options = this.$t(translationKey);
      return options.find(option => option.text === value) || '';
    },

    resetForm() {
      this.form = {
        image: null,
        name: '',
        type_assistant: this.$t('ia.type_assistant')[0],
        age: '',
        gender: '',
        intellect: '',
        mindset: '',
        occupation: '',
        primary_goal: '',
        secondary_goal: '',
        description: '',
        formality: '',
        bias: '',
        response_length: '',
      };
      this.image_preview = null;
    },

    onFileChange(evt) {
      const file = evt.target?.files?.[0];
      if (file) {
        this.assignImage(file);
      }
    },
    onDrop(e) {
      this.dragging = false;
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        this.assignImage(file);
      }
    },

    openFileDialog() {
      this.$refs.uploader.$refs['input'].click();
    },
    assignImage(file) {
      if (!file.type.startsWith('image/')) {
        showToast(this, 'danger', 'XIcon', 'ia.assistants.onlyImage', '');
        return;
      }
      this.form.image = file;

      const reader = new FileReader();
      reader.onload = (ev) => {
        this.image_preview = ev.target.result;
      };
      reader.readAsDataURL(file);
    },
    onSubmit() {
      const data = new FormData();
      
      data.append('name', this.form.name);
      data.append('type_assistant', this.form.type_assistant.value);
      
      if (this.form.image) {
        data.append('image', this.form.image);
      }

      data.append(
        'responses_ai',
        JSON.stringify({
          formality: this.form.formality,
          bias: this.form.bias,
          response_length: this.form.response_length,
        })
      );

      if (this.form.type_assistant.value === 'simplified') {
        data.append(
          'details',
          JSON.stringify({
            age: this.form.age.text,
            gender: this.form.gender.text,
            intellect: this.form.intellect.text,
            mindset: this.form.mindset.text,
            occupation: this.form.occupation.text,
          })
        );
        data.append(
          'goals',
          JSON.stringify({
            primary_goal: this.form.primary_goal.text,
            secondary_goal: this.form.secondary_goal.text,
          })
        );
      } else {
        data.append('description', this.form.description);
      }

      const serviceCall = this.isEditMode ? updateAssistant(data, this.assistant.uuid) : addAssistant(data);

      serviceCall.then((response) => {
        if (response.uuid) {
          const successMessage = this.isEditMode ? 'ia.assistants.assistantUpdated' : 'ia.assistants.addedAssistant';
          showToast(this, 'success', 'CheckCircleIcon', successMessage, '');
          this.$emit('close_sidebar', response);
          this.resetForm();
          return;
        }
        const errorMessage = this.isEditMode  ? 'ia.assistants.errorUpdatingAssistant' : 'ia.assistants.errorAddingAssistant';
        showToast(this, 'danger', 'XIcon', errorMessage, '');
      })
    }
  }
};
</script>

<style lang="scss" scoped>
.object-fit-cover { object-fit: cover; }
.background-file { background: linear-gradient(135deg, #4f9ec4, #68c78a); }
.overflow {
  // overflow: scroll;
  .container-fixed {
    position: sticky;
    bottom: 0em;
    background: white;
    border-top: 1px solid #ddd;
    z-index: 100;
    padding: 1em 0;
  }

  .container-image {
    width: 150px;
    height: 150px;
    cursor: pointer;
  }
}
</style>
